import { useState } from 'react'
import {
  Box,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  CheckboxGroup,
  VStack,
  InputGroup,
  Text as ChakraText,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  Link,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { webhookEvents } from '../../interfaces/WebhooksGetResponse'

const {
  VIDEO_TR_ACCEPTED,
  VIDEO_TR_READY,
  VIDEO_TR_ERROR,
  UPLOAD_PRE_TR_SUCCESS,
  UPLOAD_PRE_TR_ERROR,
  UPLOAD_POST_TR_SUCCESS,
  UPLOAD_POST_TR_ERROR,
} = webhookEvents

const eventCategories: { category: string; events: string[] }[] = [
  {
    category: 'Video Transformation',
    events: [VIDEO_TR_ACCEPTED, VIDEO_TR_READY, VIDEO_TR_ERROR],
  },
  {
    category: 'Pre and Post Transformations',
    events: [
      UPLOAD_PRE_TR_SUCCESS,
      UPLOAD_PRE_TR_ERROR,
      UPLOAD_POST_TR_SUCCESS,
      UPLOAD_POST_TR_ERROR,
    ],
  },
]

export const ActiveEvents = ({ activeEvents, setActiveEvents }) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')

  const handleCategoryChange = (categoryEvents, isChecked) => {
    if (isChecked) {
      setActiveEvents([
        ...activeEvents,
        ...categoryEvents.filter(event => !activeEvents.includes(event)),
      ])
    } else {
      setActiveEvents(
        activeEvents.filter(event => !categoryEvents.includes(event)),
      )
    }
  }

  const filteredCategories = searchQuery
    ? eventCategories
        .map(category => ({
          ...category,
          events: category.events.filter(event =>
            event.toLowerCase().includes(searchQuery.toLowerCase()),
          ),
        }))
        .filter(({ events }) => events.length > 0)
    : eventCategories

  return (
    <FormControl mb={4} isRequired>
      <FormLabel>{t('active_events', 'Active Events')}</FormLabel>
      <Alert
        status="info"
        minWidth="auto"
        marginBottom="1em"
        paddingBottom="0.5em"
        paddingTop="0.5em"
      >
        <AlertIcon height="1em" width="1em" />
        <AlertDescription fontSize="sm">
          {t(
            'imagekit_calls_configured_webhook_for_selected_events',
            'ImageKit calls your configured webhook for selected events. ',
          )}
          <Link
            fontSize="1em"
            href="https://imagekit.io/docs/webhooks#list-of-events"
            isExternal
          >
            {t('learn_more', 'Learn more.')}
          </Link>
        </AlertDescription>
      </Alert>
      <InputGroup mb={1}>
        <Input
          placeholder={t('search', 'Search')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </InputGroup>
      <Box
        maxHeight="200px"
        overflowY="auto"
        border="solid 0.5px"
        borderColor="gray.300"
        borderRadius="sm"
        p={1.5}
      >
        <CheckboxGroup
          colorScheme="blue"
          value={activeEvents}
          onChange={setActiveEvents}
        >
          <VStack align="start" spacing={2}>
            {filteredCategories.length ? (
              filteredCategories.map(({ category, events }, idx) => (
                <Box key={idx} width="100%">
                  <Checkbox
                    isChecked={events.every(event =>
                      activeEvents.includes(event),
                    )}
                    isIndeterminate={
                      events.some(event => activeEvents.includes(event)) &&
                      !events.every(event => activeEvents.includes(event))
                    }
                    onChange={e =>
                      handleCategoryChange(events, e.target.checked)
                    }
                  >
                    <ChakraText fontWeight="bold">{category}</ChakraText>
                  </Checkbox>
                  {events.map(event => (
                    <Checkbox key={event} value={event} pl={4}>
                      {event}
                    </Checkbox>
                  ))}
                </Box>
              ))
            ) : (
              <Box>
                <ChakraText color="gray.500" fontSize="sm">
                  {t('no_events_found', 'No events found')}
                </ChakraText>
              </Box>
            )}
          </VStack>
        </CheckboxGroup>
      </Box>
      <Flex mt={2} justifyContent="space-between" fontSize="sm">
        <ChakraText color="gray.500">
          {activeEvents.length + ' ' + t('events_selected', 'events selected')}
        </ChakraText>
        <Button
          size="xs"
          color="blue.500"
          variant="link"
          onClick={() => setActiveEvents([])}
        >
          {t('clear_all', 'Clear all')}
        </Button>
      </Flex>
    </FormControl>
  )
}

export const EndpointField = ({
  existingWebhookEndpoints,
  endpoint,
  setEndpoint,
}) => {
  const { t } = useTranslation()
  const webhookValidationSchema = Yup.object().shape({
    endpoint: Yup.string()
      .url(
        t(
          'webhook_endpoint_url_invalid',
          'Invalid URL. Enter a complete URL e.g. https://example.com/ik-webhook',
        ),
      )
      .notOneOf(
        existingWebhookEndpoints,
        t('webhook_endpoint_already_exists', 'Webhook endpoint already exists'),
      ),
  })

  const fieldError = (() => {
    try {
      webhookValidationSchema.validateSync({ endpoint })
      return ''
    } catch (error: any) {
      return error.message
    }
  })()

  return (
    <FormControl mb={4} isRequired isInvalid={Boolean(fieldError)}>
      <FormLabel>{t('endpoint_url', 'Endpoint URL')}</FormLabel>
      <Input
        type="url"
        placeholder={t(
          'webhook_endpoint_url_placeholder',
          'e.g. https://example.com/ik-webhook',
        )}
        value={endpoint}
        onChange={e => setEndpoint(e.target.value)}
      />
      <FormErrorMessage>{fieldError}</FormErrorMessage>
    </FormControl>
  )
}
